import { useState, type CSSProperties } from 'react';
import { initials } from '../lib/requestChips';
import { useAvatarUrl } from '../lib/useAvatars';
import '../styles/avatar.css';

/**
 * A person's face: their profile photo when they have a ready one, else the initials disc every
 * surface used before photos existed.
 *
 * The OUTER span is the same element with the same class as before (`req-av`, `asg-avatar`, …),
 * so its box — size, ring, stacking overlap — is unchanged whichever way it renders; the photo is
 * an `<img>` filling that box. A photo that fails to load (expired link, deleted object) falls back
 * to initials until the URL changes.
 */
export function Avatar({
  email,
  size,
  title,
  className = 'req-av',
  text,
  ariaHidden = true,
}: {
  email: string;
  /** Square side in px; omit to let `className` size it (the usual case). */
  size?: number;
  title?: string;
  /** The disc's class — also what sizes and tints it. */
  className?: string;
  /** Fallback text, when a surface computes initials its own way. Default `initials(email)`. */
  text?: string;
  ariaHidden?: boolean;
}) {
  const url = useAvatarUrl(email);
  const [failed, setFailed] = useState<string | null>(null);
  const showImg = !!url && failed !== url;
  const style: CSSProperties | undefined = size ? { width: size, height: size } : undefined;

  return (
    <span
      className={`${className}${showImg ? ' av--img' : ''}`}
      style={style}
      title={title}
      aria-hidden={ariaHidden || undefined}
      data-testid={showImg ? undefined : 'avatar-initials'}
      data-email={showImg ? undefined : email}
    >
      {showImg ? (
        <img
          className="av-img"
          src={url}
          alt={title ?? email}
          loading="lazy"
          decoding="async"
          draggable={false}
          data-testid="avatar-img"
          data-email={email}
          onError={() => setFailed(url)}
        />
      ) : (
        (text ?? initials(email))
      )}
    </span>
  );
}
